/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2022, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import {
  AnyOperand,
  DateConstraintsOperand,
  DayOfTheWeekOperand,
  ExpressionOperand,
  FromEntityValueOperand,
  ScalarOperand,
  VariableOperand
} from './operand';
import { LaunchModeResponseMode } from './skill';
import type { Entity } from './state';
import { Pagination } from '.';

export type EntityCollection = {
  entities: Entity[];
  pagination: Pagination;
};

export type EntityMention = {
  text: string;
  intent: string;
  location: number[];
};

export type EntityMentionCollection = {
  examples: EntityMention[];
  pagination: Pagination;
};

export type Mention = {
  entity: string;
  location: number[];
};

export type Example = {
  text: string;
  mentions?: Mention[];
  created?: string;
  updated?: string;
};

export type ExampleCollection = {
  examples: Example[];
  pagination: Pagination;
};

export type Intent = {
  intent: string;
  description?: string;
  created?: string;
  updated?: string;
  examples?: Example[];
  global?: any;
};

export type IntentCollection = {
  intents: Intent[];
  pagination: Pagination;
};

export enum DATA_TYPE {
  ANY = 'any',
  BOOLEAN = 'boolean',
  STRING = 'string',
  NUMBER = 'number',
  INT = 'int',
  INTEGER = 'integer',
  FLOAT = 'float',
  DOUBLE = 'double',
  DECIMAL = 'decimal',
  OBJECT = 'object',
  MAP = 'map',
  LIST = 'list',
  ARRAY = 'array',
  NULL = 'null',
  UNDEFINED = 'undefined',
  CURRENCY = 'currency',
  PERCENTAGE = 'percentage',
  TIME = 'time',
  DATE = 'date',
  DATETIME = 'datetime',
  SUBACTION_RESULT = 'subaction_result',
  YES_NO = 'yes_no',
  STREAM = 'stream'
}

export enum CONVERSATIONAL_SEARCH_DATA_TYPE {
  CONVERSATIONAL_SEARCH_RESPONSE = 'sys-synthetic-search-response-entity',
  CONVERSATIONAL_SEARCH_STATUS = 'sys-synthetic-search-status-entity',
  CONVERSATIONAL_SEARCH_CITATIONS = 'sys-synthetic-search-citations-entity'
}

export enum CONVERSATIONAL_SEARCH_VARIABLE_PATH {
  CONVERSATIONAL_SEARCH_RESPONSE = 'text',
  CONVERSATIONAL_SEARCH_STATUS = 'status',
  CONVERSATIONAL_SEARCH_CITATIONS = 'citations'
}

export type SlotFillingExample = {
  input: string;
  value: string;
};

export type DataType = {
  created?: string;
  data_type: string;
  description?: string;
  entity: string;
  response?: OptionResponse;
  title: string;
  updated?: string;
  response_type?: 'option' | 'regex'; // a property that is added by tooling ui
  num_values?: number; // a property that is added by tooling ui
};

export type AnyVariable = ExtensionVariable &
  IntegrationVariable &
  SubActionVariable &
  Variable;

export type ExtensionVariable = Variable & {
  enabled?: boolean;
  id?: string;
  items?: any;
  path?: string;
  property?: string;
  property_type?: string;
  source?: string;
  status?: string;
  stepDefinedIndex?: number;
  type?: string;
};

export type IntegrationVariable = Variable & {
  readOnly?: boolean;
  type?: string;
};

export type SubActionVariable = Variable & {
  source?: string;
  stepDefinedIndex?: number;
  stepVariable?: string;
  type?: string;
};

export type Variable = {
  category?: string;
  categoryId?: string;
  created?: string;
  data_type?: string;
  description?: string;
  entity?: string;
  expression?: string;
  icon?: any;
  index?: number;
  initial_value?: { time?: { value: string }; scalar?: string | number | null };
  default_value?: {
    scalar: string;
  };
  references?: {
    action: string;
    title: string;
    handlers: any[];
    steps: { step: string; usages: { location: string }[] }[];
  }[];
  readOnly?: boolean;
  scalar?: boolean | number | string;
  time?: {
    value: string;
  };
  title?: string;
  updated?: string;
  variable?: string;
  privacy?: {
    enabled: boolean;
  };
};

export type VariableCategory = {
  category: string;
  categoryId: string;
  children?: AnyVariable[] | null;
  stepDefinedIndex?: number;
};

type BaseResponse = {
  repeat_on_reprompt?: boolean;
};

export enum SelectionPolicy {
  SEQUENTIAL = 'sequential',
  RANDOM = 'random',
  MULTILINE = 'multiline',
  INCREMENTAL = 'incremental'
}

export type AudioResponse = {
  response_type: 'audio';
  source: string;
  title?: string;
  description?: string;
  alt_text?: string;
  channel_options?: {
    voice_telephony?: {
      loop?: string;
    };
  };
} & BaseResponse;

export type ConnectToAgentResponse = {
  response_type: 'connect_to_agent';
  message_to_human_agent: string;
  agent_available: string;
  agent_unavailable: string;
  transfer_info?: {
    target?: {
      zendesk?: { department?: { expression: string } };
      salesforce?: { button_id?: { expression: string } };
      nice_cxone?: {
        custom_data?: {
          p2?: { expression: string };
          p3?: { expression: string };
          p4?: { expression: string };
          p5?: { expression: string };
          p6?: { expression: string };
          p7?: { expression: string };
          p8?: { expression: string };
          p9?: { expression: string };
        };
      };
      amazon_connect?: {
        custom_data?: {
          undefined?: { expression: string };
        };
      };
      service_desk?: {
        department?: { expression: string };
        sip?: {
          uri?: { expression: string };
          transfer_headers_send_method?: { expression: string };
        };
      };
    };
  };
} & BaseResponse;

export type DataTypeResponse = {
  response_type: 'response_from_data_type';
} & BaseResponse;

export type DateResponse = {
  response_type: 'date';
} & BaseResponse;

// what is DTMF?!
export type DTMFResponse = {
  response_type: 'dtmf';
  command_info: { type: string; parameters?: any };
} & BaseResponse;

export type EndSessionResponse = {
  response_type: 'end_session';
  channel_options?: any;
} & BaseResponse;

export type IFrameResponse = {
  response_type: 'iframe';
  source: string;
  title?: string;
  description?: string;
  image_url?: string;
} & BaseResponse;

export type ImageResponse = {
  response_type: 'image';
  source: string;
  title?: string;
  description?: string;
  alt_text?: string;
} & BaseResponse;

export type OptionItem = {
  label: string;
  value: {
    input?: { text: string };
  };
};

export type OptionResponse = {
  response_type: 'option';
  title?: string;
  description?: string;
  preference?: 'dropdown' | 'button';
  options: OptionItem[];
} & BaseResponse;

export type PauseResponse = {
  response_type: 'pause';
  time: number;
  typing: boolean;
} & BaseResponse;

export type SpeechToTextResponse = {
  response_type: 'speech_to_text';
  command_info: { type: string; parameters?: any };
} & BaseResponse;

export type StartActivitiesResponse = {
  response_type: 'start_activities';
  activities: { type: string }[];
  channels?: { channel: string }[];
} & BaseResponse;

export type StopActivitiesResponse = {
  response_type: 'stop_activities';
  activities: { type: string }[];
  channels?: { channel: string }[];
} & BaseResponse;

export type TextResponseTextExpression = {
  text_expression: {
    concat: (
      | { scalar: string | number | boolean }
      | {
          variable: string;
          variable_path?: string;
          child_property?: string;
        }
      | { skill_variable: string }
      | { system_variable: string }
    )[];
  };
};

export type TextResponseValue = { text: string } | TextResponseTextExpression;

export type TextResponse = {
  response_type: 'text';
  values: TextResponseValue[];
  selection_policy?: SelectionPolicy;
  delimiter?: string;
} & BaseResponse;

export type TextToSpeechResponse = {
  response_type: 'text_to_speech';
  command_info: { type: string; parameters?: any };
} & BaseResponse;

export type UserDefinedResponse = {
  response_type: 'user_defined';
  user_defined: any;
} & BaseResponse;

export type VideoResponse = {
  response_type: 'video';
  source: string;
  title?: string;
  description?: string;
  alt_text?: string;
  channel_options?: { chat?: { dimensions?: { base_height: string } } };
} & BaseResponse;

export type ResponseFromVariableResponse = {
  response_type: 'response_from_variable';
} & BaseResponse;

export type GenericResponse =
  | AudioResponse
  | ConnectToAgentResponse
  | DataTypeResponse
  | DateResponse
  | DTMFResponse
  | EndSessionResponse
  | IFrameResponse
  | ImageResponse
  | OptionResponse
  | PauseResponse
  | ResponseFromVariableResponse
  | SpeechToTextResponse
  | StartActivitiesResponse
  | StopActivitiesResponse
  | TextResponse
  | TextToSpeechResponse
  | UserDefinedResponse
  | VideoResponse;

export enum LogMessageLevel {
  ERROR = 'error',
  INFO = 'info',
  WARNING = 'warning'
}

export type LogMessage = {
  level: LogMessageLevel;
  code: string;
  message: string;
};

export type OutputGenericMappingItem = {
  source: {
    action: string;
    step: string;
    type: string;
  };
};

export enum GenerativeAITask {
  CONTENT_GROUNDED_ANSWERING = 'content_grounded_answering',
  GENERAL_PURPOSE_ANSWERING = 'general_purpose_answering'
}

export const enum TurnEventType {
  ACTION_VISITED = 'action_visited',
  ACTION_FINISHED = 'action_finished',
  STEP_VISITED = 'step_visited',
  STEP_ANSWERED = 'step_answered',
  HANDLER_VISITED = 'handler_visited',
  CALLOUT = 'callout',
  CONVERSATIONAL_SKILL = 'conversational_skill',
  SEARCH = 'search',
  ACTION_ROUTING_DENIED = 'action_routing_denied',
  TOPIC_SWITCH_DENIED = 'topic_switch_denied',
  SUGGESTION_INTENTS_DENIED = 'suggestion_intents_denied',
  NODE_VISITED = 'node_visited',
  GENERATIVE_AI_CALLED_TYPE = 'generative_ai_called'
}

type TurnEventActionSource = {
  action: string;
  action_title?: string;
  type: string;
};

type TurnEventStepSource = TurnEventActionSource & {
  step: string;
  type: 'step';
};

type TurnEventHandlerSource = TurnEventStepSource & {
  handler: string;
  type: 'handler';
};

type BaseTurnEvent = {
  action_start_time?: string;
  condition_type?: string;
  event: string;
  reason: string;
  source?: TurnEventActionSource | TurnEventStepSource;
};

export type ActionVisitedTurnEvent = BaseTurnEvent & {
  event: TurnEventType.ACTION_VISITED;
  result_variable?: string;
  source: TurnEventActionSource;
};

export type ActionFinishedTurnEvent = BaseTurnEvent & {
  action_variables: Record<string, any>;
  event: TurnEventType.ACTION_FINISHED;
  result_variable?: string;
  source: TurnEventActionSource;
};

export type StepVisitedTurnEvent = BaseTurnEvent & {
  event: TurnEventType.STEP_VISITED;
  source: TurnEventStepSource;
  has_question?: boolean;
};

export type StepAnsweredTurnEvent = BaseTurnEvent & {
  event: TurnEventType.STEP_ANSWERED;
  prompted: boolean;
  source: TurnEventStepSource;
};

export type HandlerVisitedTurnEvent = BaseTurnEvent & {
  event: TurnEventType.HANDLER_VISITED;
  source: TurnEventHandlerSource;
};

export type CalloutTurnEvent = BaseTurnEvent & {
  callout: {
    internal: {
      catalog_item_id: string;
    };
    request: any;
    response: any;
    result_variable: string;
    url: string;
  };
  event: TurnEventType.CALLOUT;
  source: TurnEventStepSource;
};

export type SearchTurnEvent = BaseTurnEvent & {
  event: TurnEventType.SEARCH;
};

export type ActionRoutingDeniedTurnEvent = BaseTurnEvent & {
  event: TurnEventType.ACTION_ROUTING_DENIED;
};

export type TopicSwitchDeniedTurnEvent = BaseTurnEvent & {
  event: TurnEventType.TOPIC_SWITCH_DENIED;
};

export type SuggestionIntentsDeniedTurnEvent = BaseTurnEvent & {
  event: TurnEventType.SUGGESTION_INTENTS_DENIED;
};

export type NodeVisitedTurnEvent = BaseTurnEvent & {
  event: TurnEventType.NODE_VISITED;
};

export type TurnEvent =
  | ActionVisitedTurnEvent
  | ActionFinishedTurnEvent
  | StepVisitedTurnEvent
  | StepAnsweredTurnEvent
  | HandlerVisitedTurnEvent
  | CalloutTurnEvent
  | SearchTurnEvent
  | ActionRoutingDeniedTurnEvent
  | TopicSwitchDeniedTurnEvent
  | SuggestionIntentsDeniedTurnEvent
  | NodeVisitedTurnEvent;

export type MessageDebug = {
  auto_learn?: {
    alternate_responses?: {
      model_type: string;
    };
    disambiguation?: {
      model_type: string;
    };
  };
  log_messages?: LogMessage[];
  output_generic_mapping?: OutputGenericMappingItem[];
  turn_events?: TurnEvent[];
};

type GroupLocation = [number, number];

export type MessageEntity = {
  confidence: number;
  entity: string;
  groups?: { group: string; location: GroupLocation };
  location?: GroupLocation;
  value: string;
};

export type MessageIntent = {
  confidence: number;
  intent: string;
};

export type SuggestionResponse = {
  response_type: 'suggestion';
  suggestions: {
    label: string;
    dialog_node?: string;
    output: any;
    value: {
      input: {
        entities: MessageEntity[];
        intents: MessageIntent[];
        options?: {
          restart?: boolean;
        };
        suggestion_id: string;
        text: string;
      };
    };
  }[];
  title: string;
};

export type Output = {
  alternate_responses?: SuggestionResponse[];
  debug?: MessageDebug;
  context?: any;
  entities?: MessageEntity[];
  generic: GenericResponse[];
  intents?: MessageIntent[];
};
export type Parameter = {
  parameter: string;
  value: AnyOperand;
};

export enum ResolverType {
  CALL_AN_EXTENSION = 'callout',
  CLIENT_ACTIONS = 'client_actions',
  CONNECT_TO_AGENT = 'connect_to_agent',
  CONTINUE = 'continue',
  END_ACTION = 'end_action',
  FALLBACK = 'fallback',
  JUMP_TO = 'jump_to',
  REPLAY = 'replay',
  SEARCH = 'search',
  SEARCH_AND_END_ACTION = 'search_and_end_action',
  INVOKE_ANOTHER_ACTION = 'invoke_another_action',
  INVOKE_ANOTHER_ACTION_AND_END = 'invoke_another_action_and_end'
}

export type Resolver = {
  type: string;
  invoke_action?: {
    action?: string;
    policy?: string;
    result_variable: string;
  };
  callout?: {
    path: string;
    request_mapping?: any;
    result_variable: string;
    internal: {
      catalog_item_id: string;
      spec_hash_id?: string;
    };
    method: string;
  };
  response?: {
    filter: string;
    query: string;
    query_type: string;
  };
  client_actions?: {
    name: string;
    parameters?: Parameter[];
    result_variable: string;
  }[];
  clear?: { variable: string }[];
  step?: Step;
};

export type StepHandler = {
  created?: string;
  handler: string;
  next_handler?: string;
  output: Output;
  resolver: Resolver;
  title: string;
  type: string;
  updated?: string;
};
export type InputValidationHandler = StepHandler & {
  type: 'input_validation';
  input_constraints: InputConstraints[];
};

export type InvalidReferences = {
  callout?: number;
  data_type?: number;
  entity?: number;
  intent?: number;
  jump_to_handler?: number;
  jump_to_step?: number;
  sub_action?: number;
  value?: number;
  variable?: number;
};

export type StepContextVariableItem = AnyOperand & {
  value?: AnyOperand;
};

export type StepContext = {
  variables: StepContextVariableItem[];
};

export enum ResponseCollectionBehavior {
  OPTIONALLY_ASK = 'optionally_ask',
  ALWAYS_ASK = 'always_ask',
  NEVER_ASK = 'never_ask'
}
export type StepQuestion = {
  entity?: string;
  data_type?: string;
  free_text?: boolean;
  max_tries?: number;
  only_populate_when_prompted?: boolean;
  source_variable?: VariableReference;
  response_collection_behavior?: ResponseCollectionBehavior;
  allow_topic_switch?: boolean;
  options?: {
    value: string;
    synonyms: string[];
  }[];
  option_list?: {
    templates?: {
      label: { expression: string };
      synonyms?: { expression: string };
      value?: { expression: string };
    };
    fallback_option?: {
      label: string;
      value?: {
        input: {
          text: string;
        };
      };
    };
  };
};

export type ExistsCondition = {
  exists: VariableOperand;
};

export type NotExistsCondition = {
  not: ExistsCondition;
};

export type EqualCondition = {
  eq: [VariableOperand, AnyOperand];
};

export type NotEqualCondition = {
  neq: [VariableOperand, AnyOperand];
};

export type LessThanCondition = {
  lt: [VariableOperand, AnyOperand];
};

export type LessThanOrEqualCondition = {
  lte: [VariableOperand, AnyOperand];
};

export type GreaterThanCondition = {
  gt: [VariableOperand, AnyOperand];
};

export type GreaterThanOrEqualCondition = {
  gte: [VariableOperand, AnyOperand];
};

type CollectionConditionShape = {
  collection: ScalarOperand[] | FromEntityValueOperand[];
};

type InCondition = {
  in: [
    VariableOperand & { subtype?: 'day_of_the_week' },
    CollectionConditionShape
  ];
};

type NotInCondition = {
  not_in: [
    VariableOperand & { subtype?: 'day_of_the_week' },
    CollectionConditionShape
  ];
};

export type StringValueConditionShape = ScalarOperand & {
  options?: { ignore_case: boolean };
};

export type ContainsCondition = {
  contains: [VariableOperand, StringValueConditionShape];
};

export type NotContainsCondition = {
  not_contains: [VariableOperand, StringValueConditionShape];
};

export type MatchesCondition = {
  matches: [VariableOperand, StringValueConditionShape];
};

export type NotMatchesCondition = {
  not_matches: [VariableOperand, StringValueConditionShape];
};

export type IntentCondition = {
  intent: string;
};

export type SingleCondition =
  | ExistsCondition
  | NotExistsCondition
  | EqualCondition
  | NotEqualCondition
  | LessThanCondition
  | LessThanOrEqualCondition
  | GreaterThanCondition
  | GreaterThanOrEqualCondition
  | InCondition
  | NotInCondition
  | ContainsCondition
  | NotContainsCondition
  | MatchesCondition
  | NotMatchesCondition
  | ExpressionOperand
  | IntentCondition
  | { entity: string };

export type Condition =
  | SingleCondition
  | {
      and: SingleCondition[];
    }
  | { or: SingleCondition[] }
  | { and: Condition[] }
  | { or: Condition[] };

export type AndIntentCondition =
  | {
      and: [IntentCondition, SingleCondition];
    }
  | { and: [IntentCondition, Condition] };

export type Step = {
  condition?: Condition;
  context?: StepContext;
  created?: string;
  handlers?: StepHandler[];
  invalid_references?: InvalidReferences;
  newStep?: Step;
  next_step?: string;
  output?: Output;
  question?: StepQuestion;
  resolver: Resolver;
  responseText?: string;
  privacy?: {
    enabled: Boolean;
  };
  step: string;
  title?: string;
  updated?: string;
  variable: string;
  variableTitle?: string;
  max_hits?: number;
  examples?: Example[];
  id?: string;
};

export type BoostWord = {
  boost: string;
  phrase: string;
  type: 'string';
};

export type GenerativeAI = {
  ai_guided: {
    model_id?: string;
    content?: string;
    prompt_instruction?: string;
  };
};

export type Action = {
  action: string;
  boosts?: BoostWord[];
  condition?: Condition;
  created?: string;
  description?: string;
  disambiguation_opt_out?: boolean;
  display_name?: string;
  examples?: any[];
  handlers?: any[];
  id?: string;
  invalid_references?: InvalidReferences;
  launch_mode?: LaunchModeResponseMode;
  loading?: boolean;
  newAction?: boolean;
  next_action?: string;
  originalTitle?: string;
  registration?: {
    provider_id?: string;
    conversational_skill_id?: string;
    conversational_skill_revision_id?: string;
  };
  steps: Step[];
  sub_action_references?: any[];
  tempTitle?: string;
  title: string;
  topic_switch?: {
    allowed_from: boolean;
    allowed_into: boolean;
    never_return: boolean;
  };
  updated?: string;
  variables?: Variable[];
  generative_ai?: GenerativeAI;
  type?: string;
};

export enum ImportActionStatus {
  COMPLETED = 'Completed',
  FAILED = 'Failed',
  PROCESSING = 'Processing'
}

export type DialogNode = {
  node_id: string;
  dialog_node: string;
  type: string;
  description: string;
  conditions: string;
  output: {
    text: string;
  };
  created: string;
  updated: string;
  name: string;
  title: string;
};

export type SystemVariable = {
  description: string;
  example: string;
  read_only: boolean;
  system_variable: string;
  title: string;
  integrationVariable?: boolean;
};

type VariableReferenceOption =
  | { child_property?: string }
  | { variable_path?: string };

export type ActionVariableReference = {
  variable: string;
} & VariableReferenceOption;

export type SessionVariableReference = {
  skill_variable: string;
} & VariableReferenceOption;

export type SystemVariableReference = {
  system_variable: string;
} & VariableReferenceOption;

export type VariableReference =
  | ActionVariableReference
  | SessionVariableReference
  | SystemVariableReference;

export type MinimumInputConstraints = {
  name: 'minimum';
  value: DateConstraintsOperand | ScalarOperand;
};
export type MaximumInputConstraints = {
  name: 'maximum';
  value: DateConstraintsOperand | ScalarOperand;
};
export type DayOfTheWeekConstraints = {
  name: 'day_of_the_week';
  value: DayOfTheWeekOperand;
};
export type InputConstraints =
  | MinimumInputConstraints
  | MaximumInputConstraints
  | DayOfTheWeekConstraints;

export const enum ActionSkillTypes {
  AI_GUIDED = 'ai_guided',
  CONVERSATIONAL_SKILL = 'conversational_skill',
  STANDARD = 'standard'
}

export const enum ActionSkillCategories {
  AI_GUIDED = 'AI-guided actions',
  CONVERSATIONAL_SKILL = 'Skill-based actions',
  STANDARD = 'Custom-built actions',
  SYSTEM_ACTIONS = 'System actions',
  INTENTS = 'Intents',
  UNRECOGNIZED = 'Unrecognized',
  ALL_RECOGNIZED = 'All recognized',
  GENERAL_PURPOSE_ANSWERING = 'General purpose answering',
  CONVO_SEARCH = 'Conversational search',
  SEARCH = 'Search',
  SEARCH_ZERO_RESULTS_ONLY = 'Search zero results only'
}

export const enum AnalyticsActionSkillTypes {
  AI_GUIDED = 'ai_guided_actions',
  SKILL = 'skill_based_actions',
  CUSTOM = 'custom_built_actions',
  SYSTEM_ACTIONS = 'system_actions',
  INTENTS = 'intents',
  UNRECOGNIZED = 'unrecognized',
  ALL_RECOGNIZED = 'all_recognized',
  GENERAL_PURPOSE_ANSWERING = 'general_purpose_answering',
  CONVO_SEARCH = 'conversational_search',
  SEARCH = 'search',
  SEARCH_ZERO_RESULTS_ONLY = 'search_zero_results_only'
}

export const enum PostProcessConversationalSearchTypes {
  DISPLAY_ONLY = 'display_only',
  STORE_AND_DISPLAY = 'store_and_display',
  STORE_ONLY = 'store_only'
}
